import React, { useState, useEffect } from 'react';
import { getRequests, TestRequest, updateRequestStatus } from '../../services/requestService';
import { getActiveTestPackageById, TestPackage } from '../../services/testPackageService';
import { getActiveInsuranceById, Insurance } from '../../services/insuranceService';
import { getActiveSupplementaryInsuranceById, SupplementaryInsurance } from '../../services/supplementaryInsuranceService';
import { toast } from 'react-toastify';
import API_CONFIG from '../../config/api';

// Pagination component
const Pagination: React.FC<{
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}> = ({ currentPage, totalPages, onPageChange }) => {
  const maxPageItems = 5;
  const pages = [];

  let startPage = Math.max(1, currentPage - Math.floor(maxPageItems / 2));
  let endPage = Math.min(totalPages, startPage + maxPageItems - 1);

  if (endPage - startPage + 1 < maxPageItems) {
    startPage = Math.max(1, endPage - maxPageItems + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }

  return (
    <div className="flex justify-center mt-6">
      <nav className="flex items-center">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`mx-1 px-3 py-1 rounded ${currentPage === 1 ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          قبلی
        </button>

        {startPage > 1 && (
          <>
            <button onClick={() => onPageChange(1)} className="mx-1 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
              1
            </button>
            {startPage > 2 && <span className="mx-1">...</span>}
          </>
        )}

        {pages.map(page => (
          <button
            key={page}
            onClick={() => onPageChange(page)}
            className={`mx-1 px-3 py-1 rounded ${currentPage === page ? 'bg-indigo-600 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="mx-1">...</span>}
            <button onClick={() => onPageChange(totalPages)} className="mx-1 px-3 py-1 rounded bg-gray-200 hover:bg-gray-300 text-gray-700">
              {totalPages}
            </button>
          </>
        )}

        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`mx-1 px-3 py-1 rounded ${currentPage === totalPages ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
        >
          بعدی
        </button>
      </nav>
    </div>
  );
};

// Interface for detailed test request
interface DetailedTestRequest extends TestRequest {
  patientInfo?: {
    patientType: string;
    firstName: string;
    lastName: string;
    nationalId: string;
    mobile: string;
    gender: string;
  };
  samplingLocation?: {
    type: 'home' | 'lab';
    needsContainer?: boolean;
    freeTransportation?: boolean;
  };
  addressInfo?: {
    region: string;
    field: string;
    street: string;
    alley?: string;
    building?: string;
    unit?: string;
    number?: string;
    coordinates?: [number, number];
  };
  ageInfo?: {
    age: number;
  };
  insuranceInfo?: {
    type: string;
    nationalIdImageUrl?: string;
    testListImageUrl?: string;
    prescriptionImageUrl?: string;
    insuranceBookletImageUrl?: string;
    trackingCode?: string;
    description?: string;
    basicInsurance?: string;
    supplementaryInsurance?: string;
    selectedPackage?: string;
  };
  amount?: number; // Amount to be paid (in Toman)
  displayId?: number; // Display ID for tracking (شناسه)
  paymentExtensionRequest?: boolean; // Payment extension request
  paymentExtensionStatus?: 'pending' | 'approved' | 'rejected'; // Payment extension status
  paymentExtensionRejectionReason?: string; // Payment extension rejection reason
  statusLogs?: Array<{
    timestamp: string;
    changedBy: {
      userId: string;
      userName: string;
      userType: 'admin' | 'user';
    };
    action: string;
    oldStatus?: string;
    newStatus?: string;
    details?: string;
  }>;
}

// Helper functions
const translateStatus = (status: string, request?: TestRequest): string => {
  if (status === 'paid') {
    // Check if total amount (base + transportation) is 0 OR no transaction exists
    if (request) {
      const baseAmount = request.amount || 0;
      const transportationAmount = request.transportationPrice || 0;
      const totalAmount = baseAmount + transportationAmount;
      
      if (totalAmount === 0 || !request.transaction) {
        return 'نهایی شده';
      }
    }
    return 'نهایی و پرداخت شده';
  }
  
  const statusMap: Record<string, string> = {
    incomplete: 'ناتمام',
    canceled: 'لغو شده',
    reviewing: 'در حال بررسی',
    approved: 'تایید شده',
    rejected: 'تایید نشده',
    edited: 'ویرایش شده',
    expired: 'منقضی شده',
  };
  return statusMap[status] || status;
};

const translatePatientType = (type: string): string => {
  const typeMap: Record<string, string> = {
    self: 'خودم',
    child: 'فرزند',
    spouse: 'همسر',
    father: 'پدر',
    mother: 'مادر',
    parent: 'والدین',
    other: 'سایر'
  };
  return typeMap[type] || type;
};

const getInsuranceTypeDisplay = (type: string) => {
  switch (type) {
    case 'free': return 'آزاد';
    case 'paper': return 'دفترچه بیمه';
    case 'electronic': return 'نسخه الکترونیک';
    case 'package': return 'پکیج آزمایشگاه';
    default: return type;
  }
};

const getBasicInsuranceDisplay = (insuranceId: string | undefined, selectedInsurance: Insurance | null) => {
  if (!insuranceId) return '';
  if (insuranceId === 'none') return 'هیچکدام';
  
  if (selectedInsurance && selectedInsurance._id === insuranceId) {
    return selectedInsurance.name;
  }
  
  // If insurance data not available, show "حذف شده" (deleted)
  return 'حذف شده';
};

const getSupplementaryInsuranceDisplay = (supplementaryInsurance: string | undefined, selectedSupplementaryInsurance: SupplementaryInsurance | null) => {
  if (!supplementaryInsurance) return '';
  if (supplementaryInsurance === 'none') return 'هیچکدام';
  
  if (selectedSupplementaryInsurance && selectedSupplementaryInsurance._id === supplementaryInsurance) {
    return selectedSupplementaryInsurance.name;
  }
  
  // If insurance data not available, show "حذف شده" (deleted)
  return 'حذف شده';
};

// RequestDetails component
interface RequestDetailsProps {
  requestId: string | null;
  onClose: () => void;
  onRefresh?: () => void;
}

const RequestDetails: React.FC<RequestDetailsProps> = ({ requestId, onClose, onRefresh }) => {
  const [requestDetails, setRequestDetails] = useState<DetailedTestRequest | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<TestPackage | null>(null);
  const [selectedInsurance, setSelectedInsurance] = useState<Insurance | null>(null);
  const [selectedSupplementaryInsurance, setSelectedSupplementaryInsurance] = useState<SupplementaryInsurance | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [approvingExtension, setApprovingExtension] = useState(false);
  const [rejectingExtension, setRejectingExtension] = useState(false);

  // Function to refresh request details
  const refreshRequestDetails = async () => {
    if (!requestId) return;
    
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRequestDetails(data.data);
      }
    } catch (error) {

    }
  };

  useEffect(() => {
    if (!requestId) return;

    const fetchRequestDetails = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('لطفا ابتدا وارد حساب کاربری خود شوید.');
          setLoading(false);
          return;
        }

        const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('خطا در دریافت اطلاعات درخواست');
        }

        const data = await response.json();
        setRequestDetails(data.data);
        
        // Fetch package details if available
        if (data.data?.insuranceInfo?.selectedPackage) {
          try {
            const packageDetails = await getActiveTestPackageById(data.data.insuranceInfo.selectedPackage);
            setSelectedPackage(packageDetails);
          } catch (packageError) {

          }
        }
        
        // Fetch insurance details if available
        if (data.data?.insuranceInfo?.basicInsurance) {
          try {
            const insuranceDetails = await getActiveInsuranceById(data.data.insuranceInfo.basicInsurance);
            setSelectedInsurance(insuranceDetails);
          } catch (insuranceError) {

          }
        }

        // Fetch supplementary insurance details if available
        if (data.data?.insuranceInfo?.supplementaryInsurance && data.data.insuranceInfo.supplementaryInsurance !== 'none') {
          try {
            const supplementaryInsuranceDetails = await getActiveSupplementaryInsuranceById(data.data.insuranceInfo.supplementaryInsurance);
            setSelectedSupplementaryInsurance(supplementaryInsuranceDetails);
          } catch (supplementaryInsuranceError) {

          }
        }
      } catch (err: any) {
        setError(err.message || 'یک خطای ناشناخته رخ داد.');

      } finally {
        setLoading(false);
      }
    };

    fetchRequestDetails();
  }, [requestId]);

  const renderImagePreview = (imageUrl: string | undefined, title: string) => {
    if (!imageUrl) return null;
    return (
      <div className="mb-4">
        <h5 className="font-medium text-sm mb-2">{title}</h5>
        <div className="border border-slate-300 rounded-sm p-2 bg-slate-50">
          <img src={`${API_CONFIG.uploadURL}${imageUrl}`} alt={title} className="max-h-40 object-contain mx-auto" />
        </div>
      </div>
    );
  };

  // Handle approve payment extension
  const handleApprovePaymentExtension = async (requestIdToApprove?: string) => {
    const targetRequestId = requestIdToApprove || requestId;
    if (!targetRequestId) return;
    
    try {
      setApprovingExtension(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${targetRequestId}/approve-payment-extension`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh request details after status change
          const fetchUpdatedDetails = async () => {
            try {
              const response = await fetch(API_CONFIG.getUrl(`admin/requests/${targetRequestId}`), {
                headers: {
                  'Authorization': `Bearer ${token}`,
                },
              });

              if (response.ok) {
                const data = await response.json();
                setRequestDetails(data.data);
              }
            } catch (error) {

            }
          };
          
          fetchUpdatedDetails();
          alert('درخواست تمدید پرداخت با موفقیت تایید شد');
        } else {
          alert(data.message || 'خطا در تایید درخواست تمدید پرداخت');
        }
      } else {
        alert('خطا در ارتباط با سرور');
      }
    } catch (error) {

      alert('خطا در تایید درخواست تمدید پرداخت');
    } finally {
      setApprovingExtension(false);
    }
  };

  if (!requestId) return null;

  return (
    <div>
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
          <p>در حال بارگذاری...</p>
        </div>
      ) : error ? (
        <div className="text-center text-red-500">{error}</div>
      ) : !requestDetails ? (
        <div className="text-center text-gray-500 py-8">جزئیات درخواست یافت نشد</div>
      ) : (
        <div>
          {/* Status Display */}
          <div className="mb-6 p-4 rounded-md bg-blue-50 border border-blue-200">
            <h3 className="text-lg font-semibold text-blue-700">
              وضعیت فعلی درخواست: {translateStatus(requestDetails.status, requestDetails)}
            </h3>
          </div>

          {/* Payment Extension Request Display - Show if there was ever a payment extension request */}
          {requestDetails.paymentExtensionStatus && (
            <div className={`mb-6 p-4 rounded-md border ${
              requestDetails.paymentExtensionStatus === 'approved' 
                ? 'bg-green-50 border-green-200' 
                : requestDetails.paymentExtensionStatus === 'rejected'
                ? 'bg-red-50 border-red-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}>
              <h3 className={`text-lg font-semibold mb-2 ${
                requestDetails.paymentExtensionStatus === 'approved' 
                  ? 'text-green-700' 
                  : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'text-red-700'
                  : 'text-yellow-700'
              }`}>
                <span className="ml-2">
                  {requestDetails.paymentExtensionStatus === 'approved' 
                    ? '✅' 
                    : requestDetails.paymentExtensionStatus === 'rejected'
                    ? '❌'
                    : '⏰'
                  }
                </span>
                درخواست تمدید پرداخت
              </h3>
              <div className={`bg-white p-3 rounded border ${
                requestDetails.paymentExtensionStatus === 'approved' 
                  ? 'text-green-700 border-green-200' 
                  : requestDetails.paymentExtensionStatus === 'rejected'
                  ? 'text-red-700 border-red-200'
                  : 'text-yellow-700 border-yellow-200'
              }`}>
                {requestDetails.paymentExtensionStatus === 'approved' ? (
                  <p className="font-medium">
                    ✅ درخواست تمدید پرداخت تایید شده است.
                  </p>
                ) : requestDetails.paymentExtensionStatus === 'rejected' ? (
                  <>
                    <p className="mb-2 font-medium">
                      ❌ درخواست تمدید پرداخت رد شده است.
                    </p>
                    {requestDetails.paymentExtensionRejectionReason && (
                      <div className="mt-2 p-2 bg-red-100 rounded border border-red-300">
                        <strong className="text-sm">دلیل رد:</strong>
                        <p className="text-sm mt-1">{requestDetails.paymentExtensionRejectionReason}</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="mb-3">
                      کاربر درخواست تمدید مهلت پرداخت کرده است. آیا می‌خواهید این درخواست را تایید کنید؟
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => handleApprovePaymentExtension()}
                        disabled={approvingExtension || rejectingExtension}
                        className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-medium transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                      >
                        {approvingExtension ? 'در حال تایید...' : 'تایید درخواست تمدید پرداخت'}
                      </button>
                      <button
                        onClick={() => handleGlobalRejectPaymentExtension(requestId!, setRejectingExtension, () => {
                          // Refresh request details after rejection
                          window.location.reload();
                        }, onRefresh, refreshRequestDetails)}
                        disabled={approvingExtension || rejectingExtension}
                        className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md font-medium transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                      >
                        {rejectingExtension ? 'در حال رد...' : 'رد درخواست تمدید پرداخت'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Instructions Display - Show if instructions exist (regardless of current status) */}
          {requestDetails.instructions && (
            <div className="mb-4 p-3 rounded-md bg-green-50 border border-green-200">
              <h4 className="text-sm font-semibold text-green-700 mb-2">
                <span className="ml-2">📋</span>
                {requestDetails.status === 'approved' ? 'دستورالعمل‌ها:' : 'دستورالعمل‌های قبلی:'}
              </h4>
              <div className="text-green-700 bg-white p-2 rounded border border-green-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.instructions.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-green-600 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Rejection Reason Display - Show if rejection reason exists (regardless of current status) */}
          {requestDetails.rejectionReason && (
            <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200">
              <h4 className="text-sm font-semibold text-red-700 mb-2">
                {requestDetails.status === 'rejected' ? 'دلایل رد درخواست:' : 'دلایل رد قبلی:'}
              </h4>
              <div className="text-red-600 bg-white p-2 rounded border border-red-200 text-sm">
                <div className="space-y-1">
                  {requestDetails.rejectionReason.split('\n').map((line, index) => (
                    <div key={index} className="flex items-start">
                      <span className="text-red-500 ml-2">{line.trim().startsWith('•') ? '' : '•'}</span>
                      <span>{line.replace(/^•\s*/, '')}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Amount Display - Show if amount exists (regardless of status) */}
          {requestDetails.amount && (
            <div className="mb-4 p-3 rounded-md bg-blue-50 border border-blue-200">
              <h4 className="text-sm font-semibold text-blue-700 mb-2">
                <span className="ml-2">💰</span>
                {requestDetails.status === 'approved' ? 'هزینه قابل پرداخت:' : 'هزینه تعیین شده:'}
              </h4>
              <div className="text-blue-700 bg-white p-2 rounded border border-blue-200 text-sm">
                <div className="text-lg font-bold text-blue-800">
                  {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} تومان`}
                </div>
              </div>
            </div>
          )}
          {/* User Info Section */}
          {requestDetails.userId && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات کاربر</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.userId.name || ''} ${requestDetails.userId.lastName || ''}`}</div>
                <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.userId.nationalId || '-'}</div>
                <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.userId.mobile || '-'}</div>
              </div>
            </div>
          )}

          {/* Patient Info Section */}
          {requestDetails.patientInfo && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">مشخصات بیمار</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm">
                <div><span className="font-medium text-slate-600">نام و نام خانوادگی:</span> {`${requestDetails.patientInfo.firstName} ${requestDetails.patientInfo.lastName}`}</div>
                <div><span className="font-medium text-slate-600">کد ملی:</span> {requestDetails.patientInfo.nationalId}</div>
                <div><span className="font-medium text-slate-600">شماره موبایل:</span> {requestDetails.patientInfo.mobile}</div>
                <div><span className="font-medium text-slate-600">جنسیت:</span> {requestDetails.patientInfo.gender === 'male' ? 'مرد' : 'زن'}</div>
                <div><span className="font-medium text-slate-600">نسبت بیمار:</span> {translatePatientType(requestDetails.patientInfo.patientType)}</div>
                {requestDetails.ageInfo && (
                  <div><span className="font-medium text-slate-600">سن:</span> {requestDetails.ageInfo.age} سال</div>
                )}
              </div>
            </div>
          )}
          
          {/* Sampling Location Section */}
          {requestDetails.samplingLocation && (
            <div className="mb-8 border-b border-slate-200 pb-6">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">محل نمونه گیری</h4>
              <div className="text-sm">
                <p><span className="font-medium text-slate-600">نوع نمونه گیری:</span> 
                  {requestDetails.samplingLocation?.type === 'lab' ? ' نمونه گیری در آزمایشگاه' : ' نمونه گیری در منزل'}
                </p>
                
                {requestDetails.samplingLocation?.type === 'home' && (
                  <>
                    {requestDetails.addressInfo && (
                  <>
                    <p className="mt-2"><span className="font-medium text-slate-600">منطقه:</span> {requestDetails.addressInfo.region}</p>
                    <p>
                      {requestDetails.addressInfo.field && `میدان ${requestDetails.addressInfo.field}، `}
                      {requestDetails.addressInfo.street && `خیابان ${requestDetails.addressInfo.street}، `}
                      {requestDetails.addressInfo.alley && `کوچه ${requestDetails.addressInfo.alley}، `}
                      {requestDetails.addressInfo.building && `ساختمان ${requestDetails.addressInfo.building}، `}
                      {requestDetails.addressInfo.unit && `واحد ${requestDetails.addressInfo.unit}، `}
                      {requestDetails.addressInfo.number && `پلاک ${requestDetails.addressInfo.number}`}
                    </p>
                  </>
                    )}
                    
                    {/* Home Sampling Options */}
                    <div className="mt-3 space-y-1">
                      <div className="flex items-center">
                        <span className="font-medium text-slate-600 ml-2">نیاز به ظرف:</span>
                        {requestDetails.samplingLocation.needsContainer ? (
                          <span className="text-green-600 font-medium">✓ دارد</span>
                        ) : (
                          <span className="text-red-600 font-medium">✗ ندارد</span>
                )}
              </div>
                      <div className="flex items-center">
                        <span className="font-medium text-slate-600 ml-2">ایاب و ذهاب:</span>
                        {requestDetails.samplingLocation.freeTransportation ? (
                          <span className="text-green-600 font-medium">✓ رایگان</span>
                        ) : (
                          <span className="text-red-600 font-medium">✗ با هزینه</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
                
                {requestDetails.samplingLocation?.type === 'lab' && (
                  <p className="mt-2 text-blue-600">
                    <span className="font-medium">📍</span> بیمار باید به آزمایشگاه مراجعه کند
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Insurance Info Section */}
          {requestDetails.insuranceInfo && (
            <div className="mb-8">
              <h4 className="font-bold text-slate-700 mb-4 text-lg">اطلاعات بیمه و آزمایش</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 text-sm mb-4">
                <div><span className="font-medium text-slate-600">نوع درخواست:</span> {getInsuranceTypeDisplay(requestDetails.insuranceInfo.type)}</div>
                
                {requestDetails.insuranceInfo.type === 'paper' && (
                  <>
                    <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance, selectedInsurance)}</div>
                    {requestDetails.insuranceInfo.supplementaryInsurance && (
                      <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance, selectedSupplementaryInsurance)}</div>
                    )}
                  </>
                )}
                
                {requestDetails.insuranceInfo.type === 'electronic' && (
                  <>
                    <div><span className="font-medium text-slate-600">بیمه پایه:</span> {getBasicInsuranceDisplay(requestDetails.insuranceInfo.basicInsurance, selectedInsurance)}</div>
                    {requestDetails.insuranceInfo.supplementaryInsurance && (
                      <div><span className="font-medium text-slate-600">بیمه تکمیلی:</span> {getSupplementaryInsuranceDisplay(requestDetails.insuranceInfo.supplementaryInsurance, selectedSupplementaryInsurance)}</div>
                    )}
                    <div><span className="font-medium text-slate-600">کد رهگیری نسخه:</span> {requestDetails.insuranceInfo.trackingCode}</div>
                  </>
                )}
                
                {requestDetails.insuranceInfo.type === 'package' && selectedPackage && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-slate-600">پکیج انتخاب شده:</span>
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-sm">
                      <h5 className="font-medium text-blue-800">{selectedPackage.name}</h5>
                      {selectedPackage.description && (
                        <p className="text-sm text-blue-600 mt-1">{selectedPackage.description}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              {requestDetails.insuranceInfo.description && (
                <div className="mt-4">
                  <p className="font-medium text-slate-600 mb-1">توضیحات:</p>
                  <p className="bg-slate-50 p-3 rounded-sm border border-slate-200 text-sm">
                    {requestDetails.insuranceInfo.description}
                  </p>
                </div>
              )}
              
              {/* Uploaded Images */}
              <div className="mt-6 pt-6 border-t border-slate-200">
                <h5 className="font-bold text-slate-700 mb-4">تصاویر آپلود شده</h5>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {renderImagePreview(requestDetails.insuranceInfo.nationalIdImageUrl, 'تصویر کارت ملی')}
                  {renderImagePreview(requestDetails.insuranceInfo.testListImageUrl, 'لیست آزمایش‌ها')}
                  {renderImagePreview(requestDetails.insuranceInfo.prescriptionImageUrl, 'تصویر نسخه')}
                  {renderImagePreview(requestDetails.insuranceInfo.insuranceBookletImageUrl, 'تصویر دفترچه بیمه')}
                </div>
                {!(requestDetails.insuranceInfo.nationalIdImageUrl || 
                   requestDetails.insuranceInfo.testListImageUrl || 
                   requestDetails.insuranceInfo.prescriptionImageUrl || 
                   requestDetails.insuranceInfo.insuranceBookletImageUrl) && (
                    <p className="text-sm text-slate-500">هیچ تصویری برای این درخواست آپلود نشده است.</p>
                )}
              </div>
            </div>
          )}

          {/* System Info */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <h5 className="font-bold text-slate-700 mb-4">اطلاعات درخواست</h5>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div><span className="font-medium text-slate-600">شناسه درخواست:</span> { requestDetails.displayId || requestDetails._id.slice(-8)}</div>
              <div><span className="font-medium text-slate-600">تاریخ ایجاد:</span> {new Intl.DateTimeFormat('fa-IR').format(new Date(requestDetails.createdAt))}</div>
              <div><span className="font-medium text-slate-600">آخرین بروزرسانی:</span> {new Intl.DateTimeFormat('fa-IR').format(new Date(requestDetails.updatedAt))}</div>
              <div><span className="font-medium text-slate-600">تاریخ و ساعت پرداخت:</span> {requestDetails.paymentDateTime ? new Intl.DateTimeFormat('fa-IR', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(new Date(requestDetails.paymentDateTime)) : '-'}</div>
              {requestDetails.amount !== undefined && requestDetails.amount !== null && (
                <div><span className="font-medium text-slate-600">قیمت پایه:</span> {requestDetails.amount === 0 ? 'صفر (رایگان)' : `${new Intl.NumberFormat('fa-IR').format(requestDetails.amount)} تومان`}</div>
              )}
              {(requestDetails.status === 'approved' || requestDetails.status === 'paid') && (
                <div><span className="font-medium text-slate-600">ایاب و ذهاب:</span> {requestDetails.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد'}</div>
              )}
            </div>
          </div>

          {/* Logs */}
          {requestDetails.statusLogs && requestDetails.statusLogs.length > 0 && (
            <div className="mt-6 pt-6 border-t border-slate-200">
              <h5 className="font-bold text-slate-700 mb-4">تاریخچه تغییرات وضعیت</h5>
              <div className="space-y-3">
                {[...requestDetails.statusLogs].reverse().map((log, index) => (
                  <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                            log.changedBy.userType === 'admin' 
                              ? 'bg-purple-100 text-purple-800' 
                              : 'bg-blue-100 text-blue-800'
                          }`}>
                            {log.changedBy.userType === 'admin' ? '👨‍💼 ادمین' : '👤 کاربر'}
                          </span>
                          <span className="text-sm font-medium text-gray-900">{log.changedBy.userName}</span>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">
                          <span className="font-medium">اقدام:</span> {log.details || log.action}
                        </div>
                        {log.oldStatus && log.newStatus && (
                          <div className="text-xs text-gray-600">
                            <span className="font-medium">تغییر وضعیت:</span> {translateStatus(log.oldStatus, requestDetails)} → {translateStatus(log.newStatus, requestDetails)}
                          </div>
                        )}
                      </div>
                      <div className="text-left">
                        <div className="text-xs text-gray-500">
                          {new Intl.DateTimeFormat('fa-IR', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }).format(new Date(log.timestamp))}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function to format date
const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('fa-IR');
};

// Helper function to get status color and text
const getStatusInfo = (status: string, request?: TestRequest) => {
  switch (status) {
    case 'approved':
      return { color: 'bg-green-100 text-green-800', text: translateStatus('approved', request) };
    case 'canceled':
      return { color: 'bg-gray-100 text-gray-800', text: translateStatus('canceled', request) };
    case 'expired':
      return { color: 'bg-orange-100 text-orange-800', text: translateStatus('expired', request) };
    case 'incomplete':
      return { color: 'bg-purple-100 text-purple-800', text: translateStatus('incomplete', request) };
    case 'paid':
      return { color: 'bg-green-100 text-green-800', text: translateStatus('paid', request) };
    case 'rejected':
      return { color: 'bg-red-100 text-red-800', text: translateStatus('rejected', request) };
    default:
      return { color: 'bg-gray-100 text-gray-800', text: translateStatus(status, request) };
  }
};

// Global function to handle reject payment extension
const handleGlobalRejectPaymentExtension = async (
  requestId: string, 
  setRejectingExtension: (loading: boolean) => void,
  refreshFunction: () => void,
  onRefresh?: () => void,
  refreshDetails?: () => void
) => {
  const rejectionReason = prompt('دلیل عدم تایید درخواست تمدید پرداخت را وارد کنید (اختیاری):');
  
  // User canceled the prompt
  if (rejectionReason === null) return;
  
  try {
    setRejectingExtension(true);
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}/reject-payment-extension`), {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        rejectionReason: rejectionReason.trim() || undefined
      })
    });

    if (response.ok) {
      const data = await response.json();
      if (data.success) {
        // Refresh the table data
        refreshFunction();
        // Refresh payment extension count
        if (onRefresh) onRefresh();
        // Refresh details if provided
        if (refreshDetails) refreshDetails();
        alert('درخواست تمدید پرداخت رد شد و درخواست لغو شده است. پیام به کاربر ارسال شد.');
      } else {
        alert(data.message || 'خطا در رد درخواست تمدید پرداخت');
      }
    } else {
      alert('خطا در ارتباط با سرور');
    }
  } catch (error) {

    alert('خطا در رد درخواست تمدید پرداخت');
  } finally {
    setRejectingExtension(false);
  }
};

// Tab components
const CanceledTab: React.FC<{ onViewDetails: (requestId: string) => void; onRefresh?: () => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onRefresh, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingExtension, setApprovingExtension] = useState(false);
  const [rejectingExtension, setRejectingExtension] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;

  const fetchCanceledRequests = async () => {
    try {
      setLoading(true);
      const response = await getRequests({ 
        status: 'canceled',
        page: pagination.currentPage,
        limit: itemsPerPage,
        search: searchQuery
      });
      
      if (response.success) {
        // Sort requests: payment extension requests first, then by update date (oldest first for extensions)
        const sortedRequests = response.data.sort((a: TestRequest, b: TestRequest) => {
          // If both have payment extension requests, sort by update date (oldest first - who requested extension earlier)
          if (a.paymentExtensionRequest && b.paymentExtensionRequest) {
            return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          }
          // If only one has payment extension request, prioritize it
          if (a.paymentExtensionRequest && !b.paymentExtensionRequest) return -1;
          if (!a.paymentExtensionRequest && b.paymentExtensionRequest) return 1;
          // If neither has payment extension request, sort by creation date (newest first)
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        setRequests(sortedRequests);
        setPagination({
          currentPage: response.pagination.currentPage,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        });
        if (onResultsChange) {
          onResultsChange(response.data.length > 0);
        }
      } else {
        toast.error('خطا در دریافت درخواست‌های لغو شده');
        if (onResultsChange) {
          onResultsChange(false);
        }
      }
    } catch (error) {

      toast.error('خطا در دریافت درخواست‌های لغو شده');
      if (onResultsChange) {
        onResultsChange(false);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCanceledRequests();
  }, [pagination.currentPage, searchQuery]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Handle approve payment extension
  const handleApprovePaymentExtension = async (requestId: string) => {
    try {
      setApprovingExtension(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}/approve-payment-extension`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh the table data
          fetchCanceledRequests();
          // Refresh payment extension count
          if (onRefresh) onRefresh();
          alert('درخواست تمدید پرداخت با موفقیت تایید شد');
        } else {
          alert(data.message || 'خطا در تایید درخواست تمدید پرداخت');
        }
      } else {
        alert('خطا در ارتباط با سرور');
      }
    } catch (error) {

      alert('خطا در تایید درخواست تمدید پرداخت');
    } finally {
      setApprovingExtension(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-8">در حال بارگذاری...</div>;
  }

  return (
    <div className="mt-4">
      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-other" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-other::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-other {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        <table className="min-w-full bg-white divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">قیمت پایه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایاب و ذهاب</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ لغو</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت تمدید</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={13} className="py-8 text-center text-gray-500">
                  هیچ درخواست لغو شده‌ای یافت نشد
                </td>
              </tr>
            ) : (
              requests.map(request => {
                const statusInfo = getStatusInfo(request.status, request);
                return (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.patientInfo?.firstName} {request.patientInfo?.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.amount ? `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان` : 'رایگان') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.paymentExtensionStatus ? (
                        request.paymentExtensionStatus === 'pending' ? (
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovePaymentExtension(request._id);
                              }}
                              disabled={approvingExtension || rejectingExtension}
                              className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-medium transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                            >
                              {approvingExtension ? 'در حال تایید...' : 'تایید'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGlobalRejectPaymentExtension(request._id, setRejectingExtension, () => {
                                  // Refresh based on current tab
                                  fetchCanceledRequests();
                                }, onRefresh);
                              }}
                              disabled={approvingExtension || rejectingExtension}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded font-medium transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                            >
                              {rejectingExtension ? 'در حال رد...' : 'عدم تایید'}
                            </button>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                            request.paymentExtensionStatus === 'approved' 
                              ? 'bg-green-100 text-green-700' 
                              : request.paymentExtensionStatus === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {request.paymentExtensionStatus === 'approved' 
                              ? '✅ تایید شده' 
                              : request.paymentExtensionStatus === 'rejected'
                              ? '❌ رد شده'
                              : '⏳ در انتظار'
                            }
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.text}
                      </span>
                    </td>
                    <td className="sticky left-0 z-10 py-3 px-4 text-sm bg-white group-hover:bg-gray-50 shadow-[inset_1px_0_0_rgba(229,231,235,1)]">
                      <button 
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        onClick={() => onViewDetails(request._id)}
                      >
                        جزئیات
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

const ExpiredTab: React.FC<{ onViewDetails: (requestId: string) => void; onRefresh?: () => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onRefresh, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingExtension, setApprovingExtension] = useState(false);
  const [rejectingExtension, setRejectingExtension] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;

  useEffect(() => {
    fetchExpiredRequests();
  }, [pagination.currentPage, searchQuery]);

  const fetchExpiredRequests = async () => {
    try {
      setLoading(true);
      const response = await getRequests({ 
        status: 'expired',
        page: pagination.currentPage,
        limit: itemsPerPage,
        search: searchQuery
      });
      
      if (response.success) {
        // Sort requests: only pending payment extension requests first, then by update date (oldest first for pending)
        const sortedRequests = response.data.sort((a: TestRequest, b: TestRequest) => {
          const aIsPending = a.paymentExtensionStatus === 'pending';
          const bIsPending = b.paymentExtensionStatus === 'pending';
          
          // If both are pending, sort by update date (oldest first - who requested extension earlier)
          if (aIsPending && bIsPending) {
            return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          }
          // If only one is pending, prioritize it
          if (aIsPending && !bIsPending) return -1;
          if (!aIsPending && bIsPending) return 1;
          
          // If neither is pending, sort by creation date (newest first)
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        setRequests(sortedRequests);
        setPagination({
          currentPage: response.pagination.currentPage,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        });
        if (onResultsChange) {
          onResultsChange(response.data.length > 0);
        }
      } else {
        toast.error('خطا در دریافت درخواست‌های منقضی شده');
        if (onResultsChange) {
          onResultsChange(false);
        }
      }
    } catch (error) {

      toast.error('خطا در دریافت درخواست‌های منقضی شده');
      if (onResultsChange) {
        onResultsChange(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Handle approve payment extension
  const handleApprovePaymentExtension = async (requestId: string) => {
    try {
      setApprovingExtension(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}/approve-payment-extension`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh the table data
          fetchExpiredRequests();
          // Refresh payment extension count
          if (onRefresh) onRefresh();
          alert('درخواست تمدید پرداخت با موفقیت تایید شد');
        } else {
          alert(data.message || 'خطا در تایید درخواست تمدید پرداخت');
        }
      } else {
        alert('خطا در ارتباط با سرور');
      }
    } catch (error) {

      alert('خطا در تایید درخواست تمدید پرداخت');
    } finally {
      setApprovingExtension(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-8">در حال بارگذاری...</div>;
  }

  return (
    <div className="mt-4">
      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-other" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-other::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-other {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        <table className="min-w-full bg-white divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ انقضا</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت تمدید</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={13} className="py-8 text-center text-gray-500">
                  هیچ درخواست منقضی شده‌ای یافت نشد
                </td>
              </tr>
            ) : (
              requests.map(request => {
                const statusInfo = getStatusInfo(request.status, request);
                return (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.patientInfo?.firstName} {request.patientInfo?.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.amount ? `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان` : 'رایگان') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      {/* Show approve/reject buttons for pending requests OR old requests with paymentExtensionRequest but null status */}
                      {(request.paymentExtensionStatus === 'pending' || 
                        (request.paymentExtensionRequest && !request.paymentExtensionStatus)) ? (
                        <div className="flex gap-1">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleApprovePaymentExtension(request._id);
                            }}
                            disabled={approvingExtension || rejectingExtension}
                            className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-medium transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                          >
                            {approvingExtension ? 'در حال تایید...' : 'تایید'}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleGlobalRejectPaymentExtension(request._id, setRejectingExtension, () => {
                                // Refresh based on current tab
                                fetchExpiredRequests();
                              }, onRefresh);
                            }}
                            disabled={approvingExtension || rejectingExtension}
                            className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded font-medium transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                          >
                            {rejectingExtension ? 'در حال رد...' : 'عدم تایید'}
                          </button>
                        </div>
                      ) : request.paymentExtensionStatus ? (
                        <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                          request.paymentExtensionStatus === 'approved' 
                            ? 'bg-green-100 text-green-700' 
                            : request.paymentExtensionStatus === 'rejected'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          {request.paymentExtensionStatus === 'approved' 
                            ? '✅ تایید شده' 
                            : request.paymentExtensionStatus === 'rejected'
                            ? '❌ رد شده'
                            : '⏳ در انتظار'
                          }
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.text}
                      </span>
                    </td>
                    <td className="sticky left-0 z-10 py-3 px-4 text-sm bg-white group-hover:bg-gray-50 shadow-[inset_1px_0_0_rgba(229,231,235,1)]">
                      <button 
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        onClick={() => onViewDetails(request._id)}
                      >
                        جزئیات
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

const IncompleteTab: React.FC<{ onViewDetails: (requestId: string) => void; onRefresh?: () => void; searchQuery?: string; onResultsChange?: (hasResults: boolean) => void; onAddressClick: (addressInfo: any) => void }> = ({ onViewDetails, onRefresh, searchQuery, onResultsChange, onAddressClick }) => {
  const [requests, setRequests] = useState<TestRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [approvingExtension, setApprovingExtension] = useState(false);
  const [rejectingExtension, setRejectingExtension] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    total: 0,
    totalPages: 1
  });
  const itemsPerPage = 10;

  useEffect(() => {
    fetchIncompleteRequests();
  }, [pagination.currentPage, searchQuery]);

  const fetchIncompleteRequests = async () => {
    try {
      setLoading(true);
      const response = await getRequests({ 
        status: 'incomplete',
        page: pagination.currentPage,
        limit: itemsPerPage,
        search: searchQuery
      });
      
      if (response.success) {
        // Sort requests: payment extension requests first, then by update date (oldest first for extensions)
        const sortedRequests = response.data.sort((a: TestRequest, b: TestRequest) => {
          // If both have payment extension requests, sort by update date (oldest first - who requested extension earlier)
          if (a.paymentExtensionRequest && b.paymentExtensionRequest) {
            return new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime();
          }
          // If only one has payment extension request, prioritize it
          if (a.paymentExtensionRequest && !b.paymentExtensionRequest) return -1;
          if (!a.paymentExtensionRequest && b.paymentExtensionRequest) return 1;
          // If neither has payment extension request, sort by creation date (newest first)
          return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        });
        setRequests(sortedRequests);
        setPagination({
          currentPage: response.pagination.currentPage,
          total: response.pagination.total,
          totalPages: response.pagination.totalPages
        });
        if (onResultsChange) {
          onResultsChange(response.data.length > 0);
        }
      } else {
        toast.error('خطا در دریافت درخواست‌های ناتمام');
        if (onResultsChange) {
          onResultsChange(false);
        }
      }
    } catch (error) {

      toast.error('خطا در دریافت درخواست‌های ناتمام');
      if (onResultsChange) {
        onResultsChange(false);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  // Handle approve payment extension
  const handleApprovePaymentExtension = async (requestId: string) => {
    try {
      setApprovingExtension(true);
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl(`admin/requests/${requestId}/approve-payment-extension`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          // Refresh the table data
          fetchIncompleteRequests();
          // Refresh payment extension count
          if (onRefresh) onRefresh();
          alert('درخواست تمدید پرداخت با موفقیت تایید شد');
        } else {
          alert(data.message || 'خطا در تایید درخواست تمدید پرداخت');
        }
      } else {
        alert('خطا در ارتباط با سرور');
      }
    } catch (error) {

      alert('خطا در تایید درخواست تمدید پرداخت');
    } finally {
      setApprovingExtension(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center py-8">در حال بارگذاری...</div>;
  }

  return (
    <div className="mt-4">
      <div className="overflow-x-auto border border-gray-200 rounded custom-scrollbar-other" style={{ maxHeight: 'calc(100vh - 300px)' }}>
        <style>{`
          .custom-scrollbar-other::-webkit-scrollbar {
            height: 10px;
            width: 10px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-track {
            background: #F3F4F6;
            border-radius: 4px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-thumb {
            background: #9CA3AF;
            border-radius: 4px;
          }
          .custom-scrollbar-other::-webkit-scrollbar-thumb:hover {
            background: #6B7280;
          }
          .custom-scrollbar-other {
            scrollbar-width: thin;
            scrollbar-color: #9CA3AF #F3F4F6;
          }
          /* Sticky header and first column */
          .sticky-header {
            position: sticky;
            top: 0;
            z-index: 10;
            background: #F9FAFB;
          }
          .sticky-col {
            position: sticky;
            right: 0;
            z-index: 5;
            background: white;
          }
          .sticky-header.sticky-col {
            z-index: 15;
            background: #F9FAFB;
          }
          tbody tr:hover .sticky-col {
            background: #F9FAFB;
          }
        `}</style>
        <table className="min-w-full bg-white divide-y divide-gray-200">
          <thead>
            <tr className="bg-gray-100">
              <th className="sticky-header sticky-col py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">شناسه درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">نام و نام خانوادگی</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ثبت درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت پاسخ آزمایشگاه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">تاریخ ویرایش درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ساعت ویرایش درخواست</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">محل نمونه گیری</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">قیمت پایه</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">ایاب و ذهاب</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">آخرین بروزرسانی</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت تمدید</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">وضعیت</th>
              <th className="sticky-header py-3 px-4 text-right font-semibold text-sm text-gray-700 border-b">جزئیات</th>
            </tr>
          </thead>
          <tbody>
            {requests.length === 0 ? (
              <tr>
                <td colSpan={13} className="py-8 text-center text-gray-500">
                  هیچ درخواست ناتمام یافت نشد
                </td>
              </tr>
            ) : (
              requests.map(request => {
                const statusInfo = getStatusInfo(request.status, request);
                return (
                  <tr key={request._id} className="border-b hover:bg-gray-50 group">
                    <td className="sticky-col py-3 px-4 text-sm shadow-[inset_-1px_0_0_rgba(229,231,235,1)]">{request.displayId || request._id.slice(-8)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.patientInfo?.firstName} {request.patientInfo?.lastName}
                    </td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR').format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.createdAt))}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.labResponseDateTime ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.labResponseDateTime)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR').format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">{request.editDeadline ? new Intl.DateTimeFormat('fa-IR', {hour: '2-digit', minute: '2-digit'}).format(new Date(request.editDeadline)) : '-'}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.samplingLocation?.type === 'home' ? (
                        <div className="text-xs">
                          <div>در منزل</div>
                          {request.addressInfo && (
                            <div className="mt-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onAddressClick(request.addressInfo);
                                }}
                                className="text-right p-2 bg-blue-50 hover:bg-blue-100 rounded text-xs text-blue-700 transition-colors w-full"
                              >
                                <div className="font-medium mb-1">آدرس: (کلیک برای مشاهده کامل)</div>
                                <div className="text-gray-600 truncate">
                                  {request.addressInfo.region && `منطقه: ${request.addressInfo.region}`}
                                </div>
                              </button>
                            </div>
                          )}
                        </div>
                      ) : 'در آزمایشگاه'}
                    </td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.amount ? `${new Intl.NumberFormat('fa-IR').format(request.amount)} تومان` : 'رایگان') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{(request.status === 'approved' || request.status === 'paid') ? (request.samplingLocation?.freeTransportation ? 'رایگان' : 'دارد') : '-'}</td>
                    <td className="py-3 px-4 text-sm">{formatDate(request.updatedAt)}</td>
                    <td className="py-3 px-4 text-sm">
                      {request.paymentExtensionStatus ? (
                        request.paymentExtensionStatus === 'pending' ? (
                          <div className="flex gap-1">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprovePaymentExtension(request._id);
                              }}
                              disabled={approvingExtension || rejectingExtension}
                              className="px-2 py-1 bg-green-600 hover:bg-green-700 text-white text-xs rounded font-medium transition-colors disabled:bg-green-400 disabled:cursor-not-allowed"
                            >
                              {approvingExtension ? 'در حال تایید...' : 'تایید'}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleGlobalRejectPaymentExtension(request._id, setRejectingExtension, () => {
                                  // Refresh based on current tab
                                  fetchIncompleteRequests();
                                }, onRefresh);
                              }}
                              disabled={approvingExtension || rejectingExtension}
                              className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white text-xs rounded font-medium transition-colors disabled:bg-red-400 disabled:cursor-not-allowed"
                            >
                              {rejectingExtension ? 'در حال رد...' : 'عدم تایید'}
                            </button>
                          </div>
                        ) : (
                          <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md ${
                            request.paymentExtensionStatus === 'approved' 
                              ? 'bg-green-100 text-green-700' 
                              : request.paymentExtensionStatus === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-blue-100 text-blue-700'
                          }`}>
                            {request.paymentExtensionStatus === 'approved' 
                              ? '✅ تایید شده' 
                              : request.paymentExtensionStatus === 'rejected'
                              ? '❌ رد شده'
                              : '⏳ در انتظار'
                            }
                          </span>
                        )
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusInfo.color}`}>
                        {statusInfo.text}
                      </span>
                    </td>
                    <td className="sticky left-0 z-10 py-3 px-4 text-sm bg-white group-hover:bg-gray-50 shadow-[inset_1px_0_0_rgba(229,231,235,1)]">
                      <button 
                        className="px-3 py-1 bg-blue-100 text-blue-700 rounded text-xs hover:bg-blue-200"
                        onClick={() => onViewDetails(request._id)}
                      >
                        جزئیات
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {pagination.totalPages > 1 && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </div>
  );
};

const OtherRequests: React.FC = () => {
  const [activeTab, setActiveTab] = useState('canceled');
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);
  const [paymentExtensionCount, setPaymentExtensionCount] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  
  // State for tab visibility (when search is active)
  const [tabHasResults, setTabHasResults] = useState<Record<string, boolean>>({
    canceled: true,
    expired: true,
    incomplete: true
  });
  
  // State for address modal
  const [addressModalOpen, setAddressModalOpen] = useState<boolean>(false);
  const [selectedAddress, setSelectedAddress] = useState<TestRequest['addressInfo'] | null>(null);

  // Function to fetch payment extension count
  const fetchPaymentExtensionCount = async () => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) return;

      const response = await fetch(API_CONFIG.getUrl('/admin/requests/payment-extension-count'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPaymentExtensionCount(data.data?.paymentExtensionCount || 0);
      }
    } catch (error) {

    }
  };

  // Load payment extension count on component mount
  useEffect(() => {
    fetchPaymentExtensionCount();
  }, []);

  // Function to refresh payment extension count
  const refreshPaymentExtensionCount = () => {
    fetchPaymentExtensionCount();
  };

  // Tab switching handler
  const handleTabChange = (tabId: string) => {
    // Close any open details if switching tabs
    if (selectedRequestId) {
      setSelectedRequestId(null);
    }
    setActiveTab(tabId);
  };

  // Handle search
  const handleSearch = async () => {
    setActiveSearchQuery(searchQuery);
    
    // Check which tabs have results
    if (searchQuery.trim()) {
      await checkTabsForResults(searchQuery);
    }
  };

  // Check which tabs have results
  const checkTabsForResults = async (search: string) => {
    try {
      const statuses = ['canceled', 'expired', 'incomplete'];
      const results: Record<string, boolean> = {};
      
      for (const status of statuses) {
        try {
          const response = await getRequests({
            status,
            page: 1,
            limit: 1,
            search
          });
          results[status] = response.data.length > 0;
        } catch (error) {

          results[status] = true; // Show tab if there's an error
        }
      }
      
      setTabHasResults(results);
      
      // If current tab has no results, switch to first tab with results
      if (!results[activeTab]) {
        const firstTabWithResults = statuses.find(tab => results[tab]);
        if (firstTabWithResults) {
          setActiveTab(firstTabWithResults);
        }
      }
    } catch (error) {

    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchQuery('');
    setActiveSearchQuery('');
    // Reset all tabs to visible
    setTabHasResults({
      canceled: true,
      expired: true,
      incomplete: true
    });
  };

  // Handle search on Enter key
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Details handlers
  const handleViewDetails = (requestId: string) => {
    setSelectedRequestId(requestId);
    // Scroll to top when details modal opens
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCloseDetails = () => {
    setSelectedRequestId(null);
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">سایر درخواست‌ها</h1>

        {/* Inline Request Details (shown when a request is selected) - Full Screen Mode */}
        {selectedRequestId ? (
          <div className="bg-white border border-gray-200 rounded-lg shadow p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">جزئیات درخواست</h2>
              <button
                onClick={handleCloseDetails}
                className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-md font-medium transition-colors"
              >
                ← برگشت به لیست
              </button>
            </div>
            <RequestDetails
              requestId={selectedRequestId}
              onClose={handleCloseDetails}
              onRefresh={refreshPaymentExtensionCount}
            />
          </div>
        ) : (
          <>
            {/* Search Field */}
            <div className="mb-6">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="جستجو بر اساس نام، نام خانوادگی، کد ملی، موبایل یا شناسه..."
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={handleSearch}
                  className="px-6 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  جستجو
                </button>
                {activeSearchQuery && (
                  <button
                    onClick={handleClearSearch}
                    className="px-6 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    پاک کردن
                  </button>
                )}
              </div>
            </div>

            {/* Payment Extension Request Message */}
            {paymentExtensionCount > 0 && (
              <div className="mb-6 p-4 rounded-md bg-yellow-50 border border-yellow-200">
                <div className="flex items-center">
                  <span className="text-yellow-600 text-xl ml-2">⏰</span>
                  <div>
                    <h3 className="text-lg font-semibold text-yellow-700">
                      {paymentExtensionCount} درخواست وجود دارد که درخواست تمدید پرداخت داده‌اند
                    </h3>
                    <p className="text-yellow-600 text-sm mt-1">
                      می‌توانید در تب درخواست‌های منقضی شده مشاهده کنید
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div className="border-b border-gray-200">
              <nav className="flex -mb-px space-x-8">
                {(!activeSearchQuery || tabHasResults.canceled) && (
                  <button
                    onClick={() => handleTabChange('canceled')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ml-8 ${
                      activeTab === 'canceled'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    درخواست‌های لغو شده
                  </button>
                )}

                {(!activeSearchQuery || tabHasResults.expired) && (
                  <button
                    onClick={() => handleTabChange('expired')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ml-8 ${
                      activeTab === 'expired'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="flex items-center">
                      درخواست‌های منقضی شده
                      {paymentExtensionCount > 0 && (
                        <span className="mr-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                          {paymentExtensionCount}
                        </span>
                      )}
                    </span>
                  </button>
                )}

                {(!activeSearchQuery || tabHasResults.incomplete) && (
                  <button
                    onClick={() => handleTabChange('incomplete')}
                    className={`py-4 px-1 border-b-2 font-medium text-sm ${
                      activeTab === 'incomplete'
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    درخواست‌های ناتمام
                  </button>
                )}
              </nav>
            </div>
            
            {activeTab === 'canceled' && <CanceledTab onViewDetails={handleViewDetails} onRefresh={refreshPaymentExtensionCount} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, canceled: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
            {activeTab === 'expired' && <ExpiredTab onViewDetails={handleViewDetails} onRefresh={refreshPaymentExtensionCount} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, expired: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
            {activeTab === 'incomplete' && <IncompleteTab onViewDetails={handleViewDetails} onRefresh={refreshPaymentExtensionCount} searchQuery={activeSearchQuery} onResultsChange={(hasResults) => setTabHasResults(prev => ({ ...prev, incomplete: hasResults }))} onAddressClick={(addressInfo) => { setSelectedAddress(addressInfo); setAddressModalOpen(true); }} />}
          </>
        )}
      </div>

      {/* Address Modal */}
      {addressModalOpen && selectedAddress && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setAddressModalOpen(false)}
        >
          <div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">آدرس کامل</h3>
              <button
                onClick={() => setAddressModalOpen(false)}
                className="text-gray-500 hover:text-gray-700 text-2xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-3 text-right">
              {selectedAddress.region && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">منطقه:</span>
                  <span className="text-gray-600">{selectedAddress.region}</span>
                </div>
              )}
              {selectedAddress.field && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">میدان:</span>
                  <span className="text-gray-600">{selectedAddress.field}</span>
                </div>
              )}
              {selectedAddress.street && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">خیابان:</span>
                  <span className="text-gray-600">{selectedAddress.street}</span>
                </div>
              )}
              {selectedAddress.alley && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">کوچه:</span>
                  <span className="text-gray-600">{selectedAddress.alley}</span>
                </div>
              )}
              {selectedAddress.building && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">ساختمان:</span>
                  <span className="text-gray-600">{selectedAddress.building}</span>
                </div>
              )}
              {selectedAddress.unit && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">واحد:</span>
                  <span className="text-gray-600">{selectedAddress.unit}</span>
                </div>
              )}
              {selectedAddress.number && (
                <div className="flex">
                  <span className="font-medium text-gray-700 ml-2">پلاک:</span>
                  <span className="text-gray-600">{selectedAddress.number}</span>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-gray-200">
                <div className="font-medium text-gray-700 mb-2">آدرس کامل:</div>
                <div className="text-gray-600 leading-relaxed">
                  {[
                    selectedAddress.region && `منطقه ${selectedAddress.region}`,
                    selectedAddress.field && `میدان ${selectedAddress.field}`,
                    selectedAddress.street && `خیابان ${selectedAddress.street}`,
                    selectedAddress.alley && `کوچه ${selectedAddress.alley}`,
                    selectedAddress.building && `ساختمان ${selectedAddress.building}`,
                    selectedAddress.unit && `واحد ${selectedAddress.unit}`,
                    selectedAddress.number && `پلاک ${selectedAddress.number}`
                  ].filter(Boolean).join('، ')}
                </div>
              </div>
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setAddressModalOpen(false)}
                className="px-4 py-2 bg-indigo-500 text-white rounded-md hover:bg-indigo-600 transition-colors"
              >
                بستن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OtherRequests; 